// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';
import apiService from '../../../../share/util/api/service';
import ffdModal from '../../../vue/FFDModal';
import { createObdDtcTable, getDtcStatusResource } from '../../../../util/obd/diag';
import { OBD_FFD_LATEST_LIMIT, OBD_FFD_OLDEST_LIMIT } from '../../../../util/obd/functions';

export default {
  name: "obdDtcPage",
  props: {
    // デバッグ環境のみの動作確認
    testModeVisible: { type: Boolean, required: false }
  },
  data() {
    return {
      processing: false,
      recollectedData: [],
      measuredDataColumns: [],
      systemName: '',
      measuresId: '',
      obdDataId: '',
      ffdSystemTable: [],
      selectedSystem: null,
      ffdTitle: '',
      ffdSystem: '',
      isObd: true,
      modalFfd: false,
      measuredDataItems: [],
      dataPlayback: true,
    };
  },

  watch: {
    language() {
      this.updateColumns(this.systemName);
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
    'ffd-modal': ffdModal,
  },

  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('cache', ['cacheFFDStores']),

    /**
     * DTC計測データ一覧取得
     * @param {String} obdDataId OBD Data ID
     * @param {String} measuresId 計測データID
     * @param {String} system ECU名
     * @param {String} systemNo ECU No
     */
    async createObdDiagCodeMeasuresList(obdDataId, measuresId, system, systemNo) {
      try {
        this.systemName = system;
        this.obdDataId = obdDataId;
        this.measuresId = measuresId;
        this.processing = true;

        const params = { region_info: this.cachedProjectDataSource };
        const response = await apiService.getParamCall(
          apiService.OBD,
          `/obd/${obdDataId}/measures/${measuresId}/dtc`,
          params,
          this.operationScreen
        );

        handler.validate(handler.validateTypes.all, response, this, null, () => {
          this.recollectedData = response.data;
          this.updateColumns();
          this.updateDTCTable(response, systemNo);
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    /** 初期表示で必要な情報を設定
     */
    async updateColumns() {
      this.measuredDataColumns = [
        { name: 'status', label: 'fixed_resource.header_vehicle_dtc_table_status' },
        { name: 'dtc', label: 'fixed_resource.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'description', label: 'fixed_resource.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
        { name: 'igonCount', label: 'message.header_vehicle_dtc_table_igon_counts' },
        { name: 'igonTime', label: 'message.header_vehicle_dtc_table_igon_times' },
        { name: 'counterType', label: 'fixed_resource.header_vehicle_dtc_table_type', useResource: true },
        { name: 'ffd', label: 'fixed_resource.header_vehicle_dtc_table_ffd', useButton: true, useResource: true, originalSortFunc: diagnoseResource.sortFuncFfd },
      ];
    },

    /**
     * テーブル情報更新処理を行う
     * @param {object} response レスポンスデータ
     * @param {string} systemNo ECU No
     */
    updateDTCTable(response, systemNo) {
      // システムに紐づく DTC があるかチェックする
      let filteredDtcSystems = [];
      if (response) {
        filteredDtcSystems = response.data.data.filter(dtc => dtc.system === systemNo);
      }

      // 第3引数は使用していないが、今後の追加実装に備えて正しい値を設定する。
      this.measuredDataItems = createObdDtcTable(filteredDtcSystems, systemNo, this.systemName, this);
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },

    /**
     * ファイルエクスポートボタン押下.
     * DataRecollection.jsのcsvExportを呼び出している
     * csvExportに一覧のカラム、Itemを引数として渡している
     */
    csvExport() {
      this.$emit('csvExport', this.measuredDataColumns, this.measuredDataItems, this.recollectedData, false, true);
    },

    /**
     * FFDボタン押下.
     * DataRecollection.jsのFFDModalを呼び出している
     * @param {Array} row 一覧の選択した行
     */
    async rowButtonClickEvent(row) {
      await this.openFFDModal(row);
    },

    /**
     * FFDボタン押下.
     * @param {Object} arg 選択された行
     */
    async openFFDModal(data) {
      // this.$emit('openFFDModal', arg);
      if (!data) return;
      // タイトルの設定
      const status = data.status ? ` (${getDtcStatusResource(data.status, this)}) ` : '';
      this.ffdTitle = data.dtc + status;
      this.ffdSystem = this.systemName;
      this.ffdDescription = this.$t(data.description);
      const cache = this.cachedFFDs.filter(e => e.system === data.systemID && e.dtc === data.dtc);
      if (cache.length > 0) {
        // キャッシュ有りならキャッシュ済みデータを表示
        this.ffdSystemTable = cache[0].data;
        this.selectedSystem = data;
        this.modalFfd = true;
      } else {
        // キャッシュなしなら FFD 一覧情報を取得
        const ffdResponse = await this.getFfdResponse(data.dtc, data.systemID);
        // responseが存在しない場合、処理終了
        if (!ffdResponse) return;
        // キャッシュの更新
        this.cacheFFDStores([...this.cachedFFDs, { system: data.systemID, dtc: data.dtc, data: ffdResponse }]);
        // FFDダイアログの表示
        this.ffdSystemTable = ffdResponse;
        this.selectedSystem = data;
        this.modalFfd = true;
      }
    },

    /**
     * FFD一覧情報取得を行う関数
     * @param {string} dtc 選択したdtc
     * @param {string} systemNo systemNo
     */
    async getFfdResponse(dtc, systemNo) {
      try {
        this.processing = true;
        let latest_results = [];
        let oldest_results = [];
        let response = null;
        const params = { last_evaluated_key: null, region_info: this.cachedProjectDataSource };
        do {
          // Get APIを呼び出す
          response = await apiService.getParamCall(
            apiService.OBD,
            `/obd/${this.obdDataId}/measures/${this.measuresId}/ffd/systems/${systemNo}/codes/${dtc}`,
            params,
            this.operationScreen
          );

          // APIレスポンスの検証
          handler.validate(handler.validateTypes.all, response, this, null, () => {
            if (latest_results.length < OBD_FFD_LATEST_LIMIT) latest_results = latest_results.concat(response.data.latest);
            if (oldest_results.length < OBD_FFD_OLDEST_LIMIT) oldest_results = oldest_results.concat(response.data.oldest);
            params.last_evaluated_key = response.data.last_evaluated_key ? JSON.stringify(response.data.last_evaluated_key) : null;
          }, (result) => {
            // API レスポンスが異常だった場合
            if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
          });
          if (latest_results.length >= OBD_FFD_LATEST_LIMIT && oldest_results.length >= OBD_FFD_OLDEST_LIMIT) break;
        } while (params.last_evaluated_key);

        // 処理失敗時は response が null になるので考慮
        if (latest_results.length > 0 || oldest_results.length > 0) {
          if (latest_results.length >= OBD_FFD_LATEST_LIMIT) {
            latest_results = latest_results.slice(0, OBD_FFD_LATEST_LIMIT);
          }
          if (oldest_results.length >= OBD_FFD_OLDEST_LIMIT) {
            oldest_results = oldest_results.slice(0, OBD_FFD_OLDEST_LIMIT);
          }
          return latest_results.concat(oldest_results);
        }
        else {
          return null;
        }
      } finally {
        this.processing = false;
      }
    },

    /** DataRecollection.jsの関数を呼び出す */
    async debugExport() {
      await this.dtcTestDataExport();
    },

    /** DTCテストデータエクスポート  */
    dtcTestDataExport() {
      this.$emit('dtcTestDataExport', this.recollectedData);
    }
  },

  mounted() {
    // FFDのキャッシュクリア
    this.cacheFFDStores([]);
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedFFDs: state => state.cache.ffdStores,
      cachedDetails: state => state.cache.projectDetails,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

    /** 計測データ名 */
    measuredDataName() {
      let measuredDataName = '';
      if (this.cachedDetails?.measures) {
        const targetMeasurement = this.cachedDetails.measures[0][apiService.OBD].find(measure => measure.registered_at.toString() === this.measuresId);
        measuredDataName = targetMeasurement ? targetMeasurement.name : '';
      }
      return measuredDataName;
    }
  },
};